import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import {
  NavigationError,
  NavigationErrorHandlerFeature,
  withNavigationErrorHandler,
} from '@angular/router';

const recoveryMarkerKey = 'seech.application-bundle-recovery';
const recoveryCooldownMilliseconds = 10_000;
const staleBundleErrorFragments = [
  'chunkloaderror',
  'failed to fetch dynamically imported module',
  'failed to load module script',
  'importing a module script failed',
  'loading chunk',
];

interface RecoveryMarker {
  destination: string;
  attemptedAt: number;
}

/**
 * Recovers a router navigation when an already-open browser tab references a
 * lazy bundle that was replaced by a newer application build.
 */
@Injectable({ providedIn: 'root' })
export class ApplicationBundleRecoveryService {
  private readonly document = inject(DOCUMENT);

  recover(error: NavigationError): void {
    const browser = this.document.defaultView;
    if (!browser || browser.navigator.onLine === false || !isStaleApplicationBundleError(error.error)) {
      return;
    }

    const destination = new URL(error.url, browser.location.href).href;
    const now = Date.now();
    const priorAttempt = this.readMarker(browser);
    if (
      priorAttempt?.destination === destination &&
      now - priorAttempt.attemptedAt < recoveryCooldownMilliseconds
    ) {
      return;
    }

    this.writeMarker(browser, { destination, attemptedAt: now });
    browser.location.assign(destination);
  }

  private readMarker(browser: Window): RecoveryMarker | null {
    try {
      const value = browser.sessionStorage.getItem(recoveryMarkerKey);
      return value ? (JSON.parse(value) as RecoveryMarker) : null;
    } catch {
      return null;
    }
  }

  private writeMarker(browser: Window, marker: RecoveryMarker): void {
    try {
      browser.sessionStorage.setItem(recoveryMarkerKey, JSON.stringify(marker));
    } catch {
      // Recovery still works when storage is unavailable; only loop protection
      // is lost for that browser session.
    }
  }
}

export function withApplicationBundleRecovery(): NavigationErrorHandlerFeature {
  return withNavigationErrorHandler((error) =>
    inject(ApplicationBundleRecoveryService).recover(error),
  );
}

export function isStaleApplicationBundleError(error: unknown): boolean {
  const text = error instanceof Error
    ? `${error.name} ${error.message}`
    : typeof error === 'string'
      ? error
      : '';
  const normalized = text.toLowerCase();
  return staleBundleErrorFragments.some((fragment) => normalized.includes(fragment));
}
